/**
 * Recipe Module Constants
 * Centralized constants for recipe-related operations
 */

/**
 * Recipe status color mapping
 */
export const RECIPE_STATUS_COLORS = Object.freeze({
  Finalize: '#1890ff',
  Finalized: '#1890ff',
  Approved: '#28A745',
  Empty: '#000000',
  Pending: '#ff6d00',
  'Approval Pending': '#ff6d00',
  Rejected: '#cf1204',
  Draft: '#8c8c8c',
  Archived: '#bfbfbf',
});

/**
 * Workflow stage icons (Ant Design)
 */
export const WORKFLOW_STAGE_ICONS = Object.freeze({
  Draft: 'EditOutlined',
  'Internal Review': 'FileTextOutlined',
  Pending: 'ClockCircleOutlined',
  Approved: 'SafetyOutlined',
  Finalized: 'RocketOutlined',
  Rejected: 'CloseCircleOutlined',
  Archived: 'InboxOutlined',
});

/**
 * Recipe module types
 */
export const MODULE_TYPES = Object.freeze({
  PRODUCTION: 'prod',
  TEST: 'test',
});

/**
 * Module display names
 */
export const MODULE_DISPLAY_NAMES = Object.freeze({
  prod: 'MBMR',
  test: 'MBPR',
});

/**
 * Button types for recipe selection
 */
export const BUTTON_TYPES = Object.freeze({
  TEST: 'test',
  PRODUCTION: 'production',
  ARCHIVE: 'archive',
});

/**
 * View modes
 */
export const VIEW_MODES = Object.freeze({
  GRID: 'grid',
  LIST: 'list',
  TABLE: 'table',
});

/**
 * Pagination defaults
 */
export const PAGINATION_DEFAULTS = Object.freeze({
  PAGE_SIZE: 15,
  DEFAULT_PAGE: 1,
  SHOW_SIZE_CHANGER: false,
  SHOW_QUICK_JUMPER: true,
});

/**
 * Recipe action types
 */
export const RECIPE_ACTIONS = Object.freeze({
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  ARCHIVE: 'archive',
  COPY: 'copy',
  FINALIZE: 'finalize',
  APPROVE: 'approve',
  REJECT: 'reject',
  DOWNLOAD: 'download',
  VIEW_BOM: 'view_bom',
});

/**
 * API endpoints (relative paths)
 */
export const API_ENDPOINTS = Object.freeze({
  GET_RECIPES: '/get_all_recipe_by_product',
  GET_PRODUCTS: '/get_product_list_both',
  GET_RECIPE_STATUS: '/bp_get_pending_recipe',
  GET_PROCESS_LIST: '/get_process_list_by_recipe',
  DELETE_RECIPE: '/delete_recipe',
  ARCHIVE_RECIPE: '/archive_recipe',
  COPY_RECIPE: '/copy_recipe_data',
  FINALIZE_RECIPE: '/bp_save_recipe_status',
  DOWNLOAD_PDF: '/mbmr_overall_pdf_report',
  EDIT_MACHINE: '/edit_recipe_machine',
});

/**
 * Access control keys
 */
export const ACCESS_CONTROL_KEYS = Object.freeze({
  VIEW_MBMR: 'View MBMR',
  ARCHIVE_MBMR: 'Archive MBMR',
  ESIGN: 'ESign',
});

/**
 * Toast message durations (ms)
 */
export const TOAST_DURATION = Object.freeze({
  SUCCESS: 3000,
  ERROR: 5000,
  WARNING: 4000,
  INFO: 3000,
});

/**
 * Loading states
 */
export const LOADING_STATES = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
});

/**
 * Recipe workflow stages in order
 */
export const WORKFLOW_STAGES = Object.freeze([
  'Draft',
  'Internal Review',
  'Pending',
  'Approved',
  'Finalized',
]);

/**
 * Recipe status priorities (for sorting)
 */
export const STATUS_PRIORITY = Object.freeze({
  Pending: 1,
  'Approval Pending': 1,
  'Internal Review': 2,
  Approved: 3,
  Finalized: 4,
  Finalize: 4,
  Rejected: 5,
  Draft: 6,
  Archived: 7,
  Empty: 8,
});

/**
 * Default card/grid configurations
 */
export const GRID_CONFIG = Object.freeze({
  PRODUCT_CARD_SPAN: { xs: 24, sm: 12, md: 8, lg: 6 },
  RECIPE_CARD_SPAN: { xs: 24, sm: 12, md: 8, lg: 6 },
  GUTTER: [16, 16],
  RESPONSIVE_GUTTER: { xs: 8, sm: 12, md: 16, lg: 24 },
});

/**
 * Search debounce delay (ms)
 */
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Maximum file size for uploads (bytes)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Supported file types for recipe attachments
 */
export const SUPPORTED_FILE_TYPES = Object.freeze([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/**
 * Error messages
 */
export const ERROR_MESSAGES = Object.freeze({
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  TIMEOUT: 'Request timeout. Please try again.',
});

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = Object.freeze({
  RECIPE_CREATED: 'Recipe created successfully',
  RECIPE_UPDATED: 'Recipe updated successfully',
  RECIPE_DELETED: 'Recipe deleted successfully',
  RECIPE_ARCHIVED: 'Recipe archived successfully',
  RECIPE_COPIED: 'Recipe copied successfully',
  RECIPE_FINALIZED: 'Recipe finalized successfully',
  RECIPE_APPROVED: 'Recipe approved successfully',
});

/**
 * URL parameter keys
 */
export const URL_PARAMS = Object.freeze({
  PRODUCT_ID: 'productId',
  RECIPE_ID: 'recipeId',
  TAB_ID: 'tabId',
  VIEW_MODE: 'view',
  PAGE: 'page',
  SEARCH: 'search',
  STATUS: 'status',
});

/**
 * LocalStorage keys (for minimal usage only)
 */
export const STORAGE_KEYS = Object.freeze({
  USER_ACCESS: 'user_access_data',
  THEME: 'theme',
  LANGUAGE: 'language',
  // Avoid storing recipe/product data in localStorage
});

/**
 * Responsive breakpoints (px)
 */
export const BREAKPOINTS = Object.freeze({
  XS: 480,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1600,
});
